import { useState, useMemo, useId } from 'react';
import { format, parseISO, compareAsc } from 'date-fns';
import { Chart } from 'chart.js';
import Paper from '@mui/material/Paper';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Typography } from '@components/Typography';
import Bar from '@components/Charts/Bar';
import { ViewToggle, ViewToggleButton } from '@components/ViewToggle';
import { I18N_DATE_FORMAT, getChartDateFormat } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { styled } from '@mui/system';

const CHART_PRIMARY = '#1F4C5C';
const CHART_BAR_COLOR = 'rgba(189, 224, 204, 1)';
const CHART_TICK_FONT = { family: 'Rubik', size: 10 };
const CHART_TICK_COLOR = 'rgba(0, 0, 0, 0.6)';
const CHART_BORDER_COLOR = 'rgba(0, 0, 0, 0.3)';

const useChartId = () => useId().replace(/:/g, '');

const PaperStyled = styled(Paper)({
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    '@media print': {
        breakInside: 'avoid',
    },
});

const ChartsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
    gap: '16px',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '1fr',
    },
});

const CardHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
});

const ChartWrapper = styled('div')({
    position: 'relative',
    height: '300px',
    '@media print': {
        height: '350px !important',
    },
});

const DistributionWrapper = styled('div')({
    padding: '8px 0',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

const TopAnswersList = styled('div')({
    flex: 1,
    maxHeight: 280,
    overflowY: 'auto',
    padding: '8px 0',
});

const TopAnswerItem = styled('div')({
    display: 'flex',
    gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    '&:last-child': {
        borderBottom: 'none',
    },
});

const TEXT_TYPES = ['short_answer', 'paragraph', 'date', 'number'];
const CHOICE_TYPES = ['yes_no', 'single_choice', 'multi_select'];
const TOGGLEABLE_TYPES = ['rating', ...CHOICE_TYPES];

const STACK_COLORS = [
    '#1F4C5C',
    '#BDE0CC',
    '#98CBAD',
    '#62828D',
    '#E8C468',
    '#D4816B',
    '#7BA7BC',
    '#A3D4AE',
    '#C49BBB',
    '#6B9E78',
    '#E6A57E',
    '#8FBCCC',
    '#D1C17E',
    '#B07AA1',
    '#72B89B',
    '#C2847A',
];

const createTimeSeriesData = (timeSeries, legends, showAverage) => {
    const datasets = [
        {
            type: 'bar',
            yAxisID: 'bar',
            label: legends[0],
            data: timeSeries.map(d => d.responsesCount),
            backgroundColor: CHART_BAR_COLOR,
            hoverBackgroundColor: CHART_BAR_COLOR,
            categoryPercentage: 1.0,
            barPercentage: 0.7,
            borderWidth: 0,
            borderRadius: 8,
        },
    ];
    if (showAverage) {
        datasets.unshift({
            type: 'line',
            yAxisID: 'line',
            label: legends[1],
            data: timeSeries.map(d => d.averageRating),
            backgroundColor: CHART_PRIMARY,
            borderColor: CHART_PRIMARY,
            hoverBorderColor: CHART_PRIMARY,
            fill: false,
            borderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 12,
            pointBackgroundColor: '#BCC9CE',
            pointBorderColor: CHART_PRIMARY,
            pointBorderWidth: 1,
        });
    }
    return { labels: timeSeries.map(d => d.label), datasets };
};

const createGroupedTimeSeriesData = (labels, optionsTimeSeries) => {
    const datasets = optionsTimeSeries.map((opt, index) => ({
        label: opt.label,
        data: opt.data,
        backgroundColor: STACK_COLORS[index % STACK_COLORS.length],
        hoverBackgroundColor: STACK_COLORS[index % STACK_COLORS.length],
        categoryPercentage: 0.9,
        barPercentage: 1.0,
        borderWidth: 0,
        borderRadius: 8,
    }));
    return { labels, datasets };
};

const createGroupedChartOptions = () => ({
    scales: {
        x: { stacked: false },
        y: { stacked: false },
    },
});

const createChartOptions = (total, averageRating, showAverage) => {
    const scales = {
        y: { display: false },
        bar: {
            type: 'linear',
            position: 'left',
            border: { display: true, color: CHART_BORDER_COLOR },
            grid: { display: false, tickLength: 0, drawTicks: false },
            ticks: {
                stepSize: 5,
                font: CHART_TICK_FONT,
                color: CHART_TICK_COLOR,
                padding: 12,
            },
            title: {
                display: true,
                position: 'left',
                align: 'center',
                font: { ...CHART_TICK_FONT, weight: 500 },
                color: CHART_TICK_COLOR,
                text: total.toUpperCase(),
                padding: { bottom: 0 },
            },
        },
    };

    if (showAverage) {
        scales.line = {
            type: 'linear',
            position: 'right',
            ticks: {
                stepSize: 1,
                font: CHART_TICK_FONT,
                color: CHART_TICK_COLOR,
                padding: 10,
            },
            border: { display: true, color: CHART_BORDER_COLOR, dash: [5, 5] },
            grid: { tickLength: 0, drawTicks: false },
            title: {
                display: true,
                position: 'left',
                align: 'center',
                font: { ...CHART_TICK_FONT, weight: 500 },
                color: CHART_TICK_COLOR,
                text: averageRating.toUpperCase(),
                padding: { bottom: 0 },
            },
        };
    }

    return {
        elements: {
            point: { radius: 0 },
            line: { tension: 0.4, pointStyle: 'circle' },
        },
        plugins: { chartAreaBorder: null },
        scales,
    };
};

if (!Chart.registry.plugins.get('averageLine')) {
    Chart.register({
        id: 'averageLine',
        afterDraw(chart) {
            const avg = chart.options.plugins?.averageLine?.value;
            if (avg == null) return;
            const { ctx, chartArea, scales } = chart;
            const xScale = scales.x;
            if (!xScale) return;
            // avg is 1-based (1-5), x-axis labels are indexed 0-4
            const xPos = xScale.getPixelForValue(avg - 1);
            ctx.save();
            ctx.strokeStyle = CHART_PRIMARY;
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.beginPath();
            ctx.moveTo(xPos, chartArea.top);
            ctx.lineTo(xPos, chartArea.bottom);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = CHART_PRIMARY;
            ctx.font = '500 11px Rubik';
            ctx.textAlign = 'center';
            ctx.fillText(avg, xPos, chartArea.top - 6);
            ctx.restore();
        },
    });
}

const YES_NO_LABELS = { yes: 'FeedbackSurveys.yes', no: 'FeedbackSurveys.no' };

const DistributionChart = ({ distribution, questionType, averageRating }) => {
    const { t } = useTranslation();
    const chartId = useChartId();
    const isRating = questionType === 'rating';

    const labels = distribution.map(item => {
        if (isRating) {
            return t(`AnalyticsReviews.stars${item.label}`);
        }
        if (questionType === 'yes_no' && YES_NO_LABELS[item.label]) {
            return t(YES_NO_LABELS[item.label]);
        }
        return item.label;
    });

    const data = {
        labels,
        datasets: [
            {
                data: distribution.map(item => item.count),
                backgroundColor: CHART_BAR_COLOR,
                hoverBackgroundColor: CHART_BAR_COLOR,
                borderWidth: 0,
                borderRadius: 8,
                categoryPercentage: 0.7,
                barPercentage: 0.9,
            },
        ],
    };

    const maxCount = Math.max(...distribution.map(d => d.count), 1);
    const options = {
        plugins: {
            legend: { display: false },
            chartAreaBorder: null,
            averageLine: { value: isRating && averageRating ? parseFloat(averageRating) : null },
            tooltip: {
                callbacks: {
                    title: () => null,
                    label: context => {
                        const count = context.raw;
                        const totalCount = distribution.reduce((sum, d) => sum + d.count, 0);
                        const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
                        return `${labels[context.dataIndex]} - ${count} (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: false,
                grid: { display: false },
                border: { display: true, color: CHART_BORDER_COLOR },
                ticks: {
                    font: { family: 'Rubik', size: 11 },
                    color: CHART_TICK_COLOR,
                    callback: (_, index) => labels[index],
                },
            },
            y: {
                stacked: false,
                border: { display: true, color: CHART_BORDER_COLOR },
                grid: { tickLength: 0, drawTicks: false },
                ticks: {
                    stepSize: Math.max(1, Math.ceil(maxCount / 5)),
                    font: CHART_TICK_FONT,
                    color: CHART_TICK_COLOR,
                    padding: 8,
                },
            },
        },
    };

    return (
        <ChartWrapper id={`dist-${chartId}`}>
            <Bar parentId={`dist-${chartId}`} data={data} labels={labels} optionsProps={options} />
        </ChartWrapper>
    );
};

const TimeSeriesChart = ({ timeSeries, optionsTimeSeries, language, isRating, isChoice, grouping }) => {
    const { t } = useTranslation();
    const chartId = useChartId();
    const legends = useMemo(() => [t('AnalyticsReviews.totalResponses'), t('AnalyticsReviews.averageRating')], [t]);

    const chartData = useMemo(() => {
        if (!timeSeries || timeSeries.length === 0) return null;

        const indexed = timeSeries.map((d, i) => ({ d, i }));
        indexed.sort((a, b) => compareAsc(parseISO(a.d.date), parseISO(b.d.date)));
        const sortOrder = indexed.map(x => x.i);

        const sorted = sortOrder.map(i => timeSeries[i]);
        const labels = sorted.map(d =>
            format(parseISO(d.date), getChartDateFormat(grouping), { locale: I18N_DATE_FORMAT[language] }),
        );

        if (isChoice && optionsTimeSeries && optionsTimeSeries.length > 0) {
            const reordered = optionsTimeSeries.map(opt => ({
                ...opt,
                data: sortOrder.map(i => opt.data[i]),
            }));
            return createGroupedTimeSeriesData(labels, reordered);
        }

        const formatted = sorted.map((d, i) => ({ ...d, label: labels[i] }));
        return createTimeSeriesData(formatted, legends, isRating);
    }, [timeSeries, optionsTimeSeries, language, isRating, isChoice, legends, grouping]);

    const options = useMemo(() => {
        if (isChoice && optionsTimeSeries && optionsTimeSeries.length > 0) {
            return createGroupedChartOptions();
        }
        return createChartOptions(t('AnalyticsReviews.totalResponses'), t('AnalyticsReviews.averageRating'), isRating);
    }, [isChoice, optionsTimeSeries, isRating, t]);

    if (!chartData) return null;

    return (
        <ChartWrapper id={`ts-${chartId}`}>
            <Bar
                parentId={`ts-${chartId}`}
                data={chartData}
                labels={chartData.labels}
                legends={isChoice ? t('AnalyticsReviews.totalResponses') : undefined}
                optionsProps={options}
            />
        </ChartWrapper>
    );
};

const TextAnswers = ({ topAnswers, totalResponses }) => {
    const { t } = useTranslation();

    if (!topAnswers || topAnswers.length === 0) {
        return (
            <DistributionWrapper>
                <Typography variant="body2" sx={{ color: CHART_TICK_COLOR }}>
                    {t('AnalyticsReviews.totalResponses')}: {totalResponses}
                </Typography>
            </DistributionWrapper>
        );
    }

    return (
        <>
            <Typography variant="body2" sx={{ color: CHART_TICK_COLOR, padding: '8px 0 4px' }}>
                {t('AnalyticsReviews.totalResponses')}: {totalResponses}
            </Typography>
            <TopAnswersList>
                {topAnswers.map((answer, index) => (
                    <TopAnswerItem key={index}>
                        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>
                            {index + 1}.
                        </Typography>
                        <Typography variant="body2" sx={{ color: CHART_TICK_COLOR, wordBreak: 'break-word' }}>
                            {answer}
                        </Typography>
                    </TopAnswerItem>
                ))}
            </TopAnswersList>
        </>
    );
};

const QuestionCard = ({ title, questionData, language, grouping }) => {
    const qType = questionData.questionType || 'rating';
    const options = questionData.options || [];
    const total = questionData.totalResponses || 0;
    const timeSeries = questionData.timeSeries;
    const optionsTimeSeries = questionData.optionsTimeSeries;
    const topAnswers = questionData.topAnswers;
    const isTextType = TEXT_TYPES.includes(qType);
    const isChoice = CHOICE_TYPES.includes(qType);
    const hasDistribution = options.length > 0;
    const hasTimeline = timeSeries && timeSeries.length > 0;
    const isToggleable = TOGGLEABLE_TYPES.includes(qType) && hasDistribution && hasTimeline;
    const isRating = qType === 'rating';
    const [view, setView] = useState(hasDistribution ? 'distribution' : 'timeline');

    const averageRating = useMemo(() => {
        if (!isRating || !options.length || total === 0) return null;
        const sum = options.reduce((acc, opt) => acc + Number(opt.label) * opt.count, 0);
        return (sum / total).toFixed(1);
    }, [isRating, options, total]);

    return (
        <PaperStyled elevation={1}>
            <CardHeader>
                <Typography variant="h6" sx={{ color: 'rgba(0,0,0, 0.87)' }}>
                    {title}
                </Typography>
                {isToggleable && (
                    <ViewToggle>
                        <ViewToggleButton active={view === 'distribution'} onClick={() => setView('distribution')}>
                            <BarChartIcon fontSize="small" />
                        </ViewToggleButton>
                        <ViewToggleButton active={view === 'timeline'} onClick={() => setView('timeline')}>
                            <TimelineIcon fontSize="small" />
                        </ViewToggleButton>
                    </ViewToggle>
                )}
            </CardHeader>
            {isTextType ? (
                <TextAnswers topAnswers={topAnswers} totalResponses={total} />
            ) : view === 'timeline' ? (
                <TimeSeriesChart
                    timeSeries={timeSeries}
                    optionsTimeSeries={optionsTimeSeries}
                    language={language}
                    isRating={isRating}
                    isChoice={isChoice}
                    grouping={grouping}
                />
            ) : (
                <DistributionChart distribution={options} questionType={qType} averageRating={averageRating} />
            )}
        </PaperStyled>
    );
};

const Questions = ({ data, questionDistributions, language, grouping }) => {
    const unifiedQuestions = useMemo(() => {
        const items = [];
        const hasDistributions = questionDistributions && Object.keys(questionDistributions).length > 0;

        if (hasDistributions) {
            Object.entries(questionDistributions).forEach(([title, qd]) => {
                items.push({ title, questionData: qd });
            });
        }

        if (data && Object.keys(data).length > 0) {
            Object.entries(data).forEach(([title, value]) => {
                if (items.some(item => item.title === title)) return;
                const sortedValue = [...value].sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));
                items.push({
                    title,
                    questionData: {
                        questionType: 'rating',
                        options: [],
                        totalResponses: sortedValue.reduce((sum, d) => sum + d.responsesCount, 0),
                        timeSeries: sortedValue,
                    },
                });
            });
        }

        return items;
    }, [questionDistributions, data]);

    if (unifiedQuestions.length === 0) return null;

    return (
        <ChartsGrid>
            {unifiedQuestions.map(item => (
                <QuestionCard
                    key={item.title}
                    title={item.title}
                    questionData={item.questionData}
                    language={language}
                    grouping={grouping}
                />
            ))}
        </ChartsGrid>
    );
};

export default Questions;
